import { useCallback, useEffect, useState } from "react";
import { reportClientError } from "../../application/client-error-logger";
import { emptyHomeDashboardState, HomeDashboardRepository, type HomeDashboardState } from "../../application/home-dashboard";
import { localISODate } from "../../application/water-repository";
import { shiftISODate } from "../calendar/day-labels";

export function useDashboard(dashboardRepository: HomeDashboardRepository) {
  const currentDate = localISODate();
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [dashboard, setDashboard] = useState(() => emptyHomeDashboardState(currentDate));
  const [previousDashboard, setPreviousDashboard] = useState(() => emptyHomeDashboardState(shiftISODate(currentDate, -1)));
  const [nextDashboard, setNextDashboard] = useState<HomeDashboardState | null>(null);
  const [dashboardStatus, setDashboardStatus] = useState<"loading" | "ready" | "saving" | "error">("loading");
  const refreshDashboard = useCallback(async () => {
    try {
      setDashboard(await dashboardRepository.get(selectedDate));
      setDashboardStatus("ready");
    } catch (error) {
      reportClientError(error, { area: "dashboard", action: "load", date: selectedDate });
      setDashboardStatus("error");
    }
  }, [dashboardRepository, selectedDate]);

  useEffect(() => {
    setDashboardStatus("loading");
    void refreshDashboard();
    return dashboardRepository.subscribe(selectedDate, () => { void refreshDashboard(); });
  }, [dashboardRepository, refreshDashboard, selectedDate]);
  useEffect(() => {
    const previousDate = shiftISODate(selectedDate, -1);
    const nextDate = shiftISODate(selectedDate, 1);
    void dashboardRepository.get(previousDate).then(setPreviousDashboard).catch(() => setPreviousDashboard(emptyHomeDashboardState(previousDate)));
    if (nextDate <= currentDate) void dashboardRepository.get(nextDate).then(setNextDashboard).catch(() => setNextDashboard(emptyHomeDashboardState(nextDate)));
    else setNextDashboard(null);
  }, [dashboardRepository, currentDate, selectedDate]);

  return { currentDate, selectedDate, setSelectedDate, dashboard, setDashboard, previousDashboard, nextDashboard, dashboardStatus, setDashboardStatus, refreshDashboard };
}
